import React, { useState, useEffect, useRef } from 'react';
import { trackEvent } from '../utils/analytics';
import { useCloudSave } from '../hooks/useCloudSave';
import { useAuth } from '../hooks/useAuth';
import { useWhiteboardStore } from '../store/useWhiteboardStore';
import { validateCanvasName, generateDefaultCanvasName } from '../utils/cloudSaveUtils';
import { useFeatureAccess } from '../hooks/useQuota';
import ConfirmationDialog from './ConfirmationDialog';
import './SaveTreeModal.css';

interface SaveTreeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (filename: string) => void;
  onCloudSave?: (canvasName: string, description?: string, tags?: string[]) => Promise<void>;
  yamlContent?: string;
}

const SaveTreeModal: React.FC<SaveTreeModalProps> = ({
  isOpen,
  onClose,
  onSave,
  onCloudSave,
  yamlContent
}) => {
  const { user } = useAuth();
  const { saveCanvas, loading: cloudLoading, error: cloudError, clearError } = useCloudSave();
  const { canCreateCanvas } = useFeatureAccess();
  const { state: whiteboardState } = useWhiteboardStore();
  const { canvasName: currentCanvasName, canvasId: currentCanvasId, description: currentDescription, tags: currentTags } = whiteboardState;
  const setStoreCanvasName = useWhiteboardStore(state => state.setCanvasName);
  
  const [filename, setFilename] = useState('thread-it');
  const [canvasName, setCanvasName] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState('');
  const [saveMode, setSaveMode] = useState<'local' | 'cloud'>('local');
  const [exportFormat] = useState<'json'>('json');
  const [validationError, setValidationError] = useState<string | null>(null);
  const [localSaving, setLocalSaving] = useState(false);
  
  // Confirmation dialog state
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingSaveData, setPendingSaveData] = useState<{
    content: string;
    canvasName: string;
    description: string;
    tags: string[];
  } | null>(null);
  
  const inputRef = useRef<HTMLInputElement>(null);
  const canvasNameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      if (user) {
        setSaveMode('cloud');
      } else {
        setSaveMode('local');
      }
    }
  }, [isOpen, user]);

  useEffect(() => {
    if (isOpen) {
      // Reset form when modal opens - use current canvas name if available
      const defaultName = currentCanvasName || generateDefaultCanvasName();
      
      setCanvasName(defaultName);
      setFilename(currentCanvasName || 'thread-it');
      setDescription(currentDescription || '');
      setTags(currentTags?.join(', ') || '');
      setValidationError(null);
      clearError();
      
      // Focus the appropriate input based on save mode
      setTimeout(() => {
        if (saveMode === 'local') {
          inputRef.current?.focus();
          inputRef.current?.select();
        } else {
          canvasNameRef.current?.focus();
          canvasNameRef.current?.select();
        }
      }, 100);
    }
  }, [isOpen, saveMode, clearError, currentCanvasName, currentDescription, currentTags]);

  const handleSave = () => {
    if (saveMode === 'local') {
      if (filename.trim()) {
        setLocalSaving(true);
        
        // Dispatch saving event to show animation
        window.dispatchEvent(new CustomEvent('whiteboard-saving', { 
          detail: { type: 'local', filename: filename.trim() } 
        }));

        // Update canvas name in store BEFORE saving to ensure state consistency
        setStoreCanvasName(filename.trim());
        
        // Generate fresh JSON content AFTER updating canvas name and save locally
        const generateFreshContent = async () => {
          try {
            const { exportTreeToJSON, downloadJSON } = await import('../utils/treeExporter');
            const currentState = useWhiteboardStore.getState().state;
            
            const content = exportTreeToJSON(currentState);
            downloadJSON(content, filename.trim());
            
            trackEvent('tree_saved', { tree_name: filename.trim(), format: 'json' });
            
            // Dispatch save event for status indicator
            const saveEventDetail = { type: 'local', filename: filename.trim(), format: 'json' };
            window.dispatchEvent(new CustomEvent('whiteboard-saved', { 
              detail: saveEventDetail
            }));
            onClose();
          } catch (error) {
            console.error('Failed to save file:', error);
            setValidationError('Failed to save file');
            setLocalSaving(false);
          }
        };
        
        generateFreshContent();
      }
    } else {
      handleCloudSave();
    }
  };

  const handleCloudSave = async () => {
    // Validate canvas name
    const validation = validateCanvasName(canvasName);
    if (!validation.isValid) {
      setValidationError(validation.error || 'Invalid canvas name');
      return;
    }

    setValidationError(null);

    try {
      // Generate fresh JSON content
      const { exportTreeToJSON } = await import('../utils/treeExporter');
      const currentState = useWhiteboardStore.getState().state;
      const freshContent = exportTreeToJSON(currentState);
      
      const parsedTags = tags.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0);
      
      // Check if canvas name changed from current active canvas
      const isNameChanged = canvasName.trim() !== currentCanvasName;
      
      // Determine if this will create a new canvas
      const willCreateNewCanvas = isNameChanged || !currentCanvasId;
      
      if (willCreateNewCanvas) {
        // This will create a new canvas - check quota first
        try {
          const canCreate = await canCreateCanvas();
          if (!canCreate) {
            setValidationError('You\'ve reached your canvas limit. Please upgrade your plan to create more canvases.');
            return;
          }
        } catch (quotaError) {
          console.error('Error checking canvas creation quota:', quotaError);
          // Continue with canvas creation if quota check fails
        }
        
        if (isNameChanged && currentCanvasId) {
          // Name changed and we have a current canvas - show confirmation dialog
          setPendingSaveData({
            content: freshContent,
            canvasName: canvasName.trim(),
            description: description.trim(),
            tags: parsedTags
          });
          setShowConfirmation(true);
          return;
        }
      }
      
      // Proceed with save (either same name overwrite or new canvas creation)
      await performCloudSave(freshContent, canvasName.trim(), description.trim(), parsedTags);
      
    } catch (error) {
      setValidationError('An unexpected error occurred');
    }
  };

  const performCloudSave = async (
    content: string, 
    canvasName: string, 
    description: string, 
    tags: string[]
  ) => {
    try {
      // Dispatch saving event to show animation
      window.dispatchEvent(new CustomEvent('whiteboard-saving', { 
        detail: { type: 'cloud', canvasName: canvasName } 
      }));

      // Update canvas name in store BEFORE saving to ensure state consistency
      setStoreCanvasName(canvasName);
      
      // Determine canvasId based on name change
      const isNameChanged = canvasName !== currentCanvasName;
      const canvasIdToUse = isNameChanged ? undefined : currentCanvasId;
      
      const result = await saveCanvas(
        content,
        canvasName,
        canvasIdToUse, // Pass undefined for new canvas, currentId for overwrite
        description || undefined,
        tags.length > 0 ? tags : undefined
      );

      if (result.success) {
        trackEvent('tree_saved_to_cloud', { tree_name: canvasName });
        
        // Dispatch save event for status indicator
        const saveEventDetail = { type: 'cloud', canvasName: canvasName, canvasId: result.canvasId };
        
        // After a successful save, if it was a *new* canvas, we get back a new canvasId.
        // We should update our store to reflect this so the *next* save is an update.
        if (result.canvasId && result.canvasId !== currentCanvasId) {
          useWhiteboardStore.getState().setCanvasId(result.canvasId);
        }

        window.dispatchEvent(new CustomEvent('whiteboard-saved', { 
          detail: saveEventDetail
        }));
        onClose();
      } else {
        setValidationError(result.error || 'Failed to save to cloud');
      }
    } catch (error) {
      setValidationError('An unexpected error occurred');
    }
  };

  const handleConfirmationConfirm = async () => {
    if (pendingSaveData) {
      setShowConfirmation(false);
      setPendingSaveData(null);
      await performCloudSave(
        pendingSaveData.content,
        pendingSaveData.canvasName,
        pendingSaveData.description,
        pendingSaveData.tags
      );
    }
  };

  const handleConfirmationCancel = () => {
    setShowConfirmation(false);
    setPendingSaveData(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  const handleModeChange = (mode: 'local' | 'cloud') => {
    setSaveMode(mode);
    setValidationError(null);
    clearError();
  };

  const isSaveDisabled = () => {
    if (saveMode === 'local') {
      return !filename.trim() || localSaving;
    } else {
      return !canvasName.trim() || cloudLoading;
    }
  };

  const getSaveButtonText = () => {
    if (saveMode === 'local') {
      return localSaving ? 'Saving...' : 'Save';
    } else {
      return cloudLoading ? 'Saving...' : 'Save';
    }
  };

  const isSaving = () => {
    return localSaving || cloudLoading;
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="save-tree-modal-backdrop" onClick={handleBackdropClick}>
        <div className="save-tree-modal">
          <div className="save-tree-modal-content">
            <h3>Save Tree</h3>
            <p className="save-tree-description">
              Save your entire thread to a file
            </p>

            {/* Save Mode Tabs */}
            <div className="save-mode-tabs">
              <button
                className={`save-mode-tab ${saveMode === 'local' ? 'active' : ''}`}
                onClick={() => handleModeChange('local')}
              >
                💾 Local File
              </button>
              <button
                className={`save-mode-tab ${saveMode === 'cloud' ? 'active' : ''}`}
                onClick={() => handleModeChange('cloud')}
                disabled={!user}
              >
                ☁️ Cloud Save
              </button>
            </div>

            {/* Error Display */}
            {(validationError || cloudError) && (
              <div className="save-error-message">
                {validationError || cloudError}
              </div>
            )}

            {/* Local Save Form */}
            {saveMode === 'local' && (
              <div className="save-form-section">
                <div className="filename-input-group">
                  <label htmlFor="filename-input">Filename:</label>
                  <input
                    ref={inputRef}
                    id="filename-input"
                    type="text"
                    value={filename}
                    onChange={(e) => setFilename(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Enter filename..."
                    className="save-tree-input"
                  />
                  <span className="file-extension">.json</span>
                </div>
              </div>
            )}

            {/* Cloud Save Form */}
            {saveMode === 'cloud' && (
              <div className="save-form-section">
                {!user ? (
                  <div className="cloud-save-login-prompt">
                    <p>Please log in to save to the cloud</p>
                  </div>
                ) : (
                  <>
                    <div className="cloud-save-field">
                      <label htmlFor="canvas-name-input">Canvas Name:</label>
                      <input
                        ref={canvasNameRef}
                        id="canvas-name-input"
                        type="text"
                        value={canvasName}
                        onChange={(e) => setCanvasName(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="Enter canvas name..."
                        className="save-tree-input"
                      />
                    </div>
                    
                    <div className="cloud-save-field">
                      <label htmlFor="description-input">Description (optional):</label>
                      <textarea
                        id="description-input"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="Describe your canvas..."
                        className="save-tree-textarea"
                        rows={2}
                      />
                    </div>
                    
                    <div className="cloud-save-field">
                      <label htmlFor="tags-input">Tags (optional):</label>
                      <input
                        id="tags-input"
                        type="text"
                        value={tags}
                        onChange={(e) => setTags(e.target.value)}
                        placeholder="Enter tags separated by commas..."
                        className="save-tree-input"
                      />
                    </div>
                  </>
                )}
              </div>
            )}

            <div className="save-tree-actions">
              <button 
                className="save-tree-save-btn"
                onClick={handleSave}
                disabled={isSaveDisabled()}
              >
                {getSaveButtonText()}
              </button>
              <button 
                className="save-tree-cancel-btn"
                onClick={onClose}
                disabled={isSaving()}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Dialog for New Canvas Creation */}
      <ConfirmationDialog
        isOpen={showConfirmation}
        title="Create New Canvas?"
        message={`You're changing the canvas name from '${currentCanvasName}' to '${pendingSaveData?.canvasName}'. This will create a new canvas instead of updating the current one. Continue?`}
        confirmText="Create New Canvas"
        cancelText="Cancel"
        confirmVariant="primary"
        onConfirm={handleConfirmationConfirm}
        onCancel={handleConfirmationCancel}
      />
    </>
  );
};

export default SaveTreeModal; 